import React from "react";
import { cn } from "../lib/utils";
import Glow from "./Glow";

type Intensity = "soft" | "vivid";
interface Props { className?: string; intensity?: Intensity }

// Full-bleed, soft multi-layer gradient background f
export default function SectionBg({ className, intensity = "soft" }: Props) {
  // Subtle per-mount randomization 
  const seed = React.useMemo(() => Math.random(), []);
  const rnd = React.useMemo(() => {
    let t = seed * 1000 + 1;
    return () => {
      // simple pseudo-random generator based on sine
      t = Math.sin(t) * 10000;
      return t - Math.floor(t);
    };
  }, [seed]);

  const tint1 = React.useMemo(() => ({
    top: -60 - Math.floor(rnd() * 40),
    left: -70 - Math.floor(rnd() * 40),
    opacity: intensity === "vivid" ? 0.18 + rnd() * 0.06 : 0.10 + rnd() * 0.04,
  }), [rnd, intensity]);
  const tint2 = React.useMemo(() => ({
    bottom: -70 - Math.floor(rnd() * 40),
    right: -60 - Math.floor(rnd() * 40),
    opacity: intensity === "vivid" ? 0.22 + rnd() * 0.08 : 0.12 + rnd() * 0.05,
  }), [rnd, intensity]);
  const tint3 = React.useMemo(() => ({
    top: Math.floor(20 + rnd() * 30) + "%",
    left: Math.floor(25 + rnd() * 30) + "%",
    opacity: intensity === "vivid" ? 0.20 + rnd() * 0.06 : 0.12 + rnd() * 0.05,
  }), [rnd, intensity]);

  // Extra small blobs for richer color distribution
  const extra1 = React.useMemo(() => ({
    top: Math.floor(10 + rnd() * 20) + "%",
    right: Math.floor(5 + rnd() * 15) + "%",
    opacity: intensity === "vivid" ? 0.14 + rnd() * 0.06 : 0.08 + rnd() * 0.04,
  }), [rnd, intensity]);
  const extra2 = React.useMemo(() => ({
    bottom: Math.floor(15 + rnd() * 25) + "%",
    left: Math.floor(8 + rnd() * 18) + "%",
    opacity: intensity === "vivid" ? 0.14 + rnd() * 0.06 : 0.08 + rnd() * 0.04,
  }), [rnd, intensity]);
  const extra3 = React.useMemo(() => ({
    top: Math.floor(55 + rnd() * 20) + "%",
    left: Math.floor(60 + rnd() * 20) + "%",
    opacity: intensity === "vivid" ? 0.12 + rnd() * 0.06 : 0.06 + rnd() * 0.04,
  }), [rnd, intensity]);

  // Parallax: track scroll and apply gentle offsets
  const [par, setPar] = React.useState(0);
  React.useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      const y = window.scrollY || 0;
      // normalize small movement
      raf = window.requestAnimationFrame(() => setPar(y));
    };
    const opts = { passive: true } as AddEventListenerOptions;
    window.addEventListener("scroll", onScroll, opts);
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  // scale per layer
  const p1 = (par * 0.02);
  const p2 = (par * 0.015);
  const p3 = (par * 0.01);
  return (
    <div className={cn("pointer-events-none absolute inset-0 z-0 overflow-hidden", className)}>
      {/* layered glows */}
      <Glow variant="above" className={cn(intensity === "vivid" ? "opacity-80" : "opacity-60")} />
      <Glow variant="center" className={cn(intensity === "vivid" ? "opacity-80" : "opacity-70")} />
      <Glow variant="below" className={cn(intensity === "vivid" ? "opacity-70" : "opacity-60")} />

      {/* corner color tints */}
      <div
        className={cn("absolute h-96 w-96 rounded-full blur-[100px] ps-anim-float", intensity === "vivid" ? "bg-indigo-500" : "bg-indigo-500")}
        style={{ top: `${tint1.top}px`, left: `${tint1.left}px`, opacity: tint1.opacity, transform: `translate3d(0, ${p1}px, 0)` }}
      />
      <div
        className={cn("absolute h-96 w-96 rounded-full blur-[100px] ps-anim-float-2", intensity === "vivid" ? "bg-fuchsia-500" : "bg-fuchsia-500")}
        style={{ bottom: `${tint2.bottom}px`, right: `${tint2.right}px`, opacity: tint2.opacity, transform: `translate3d(0, ${-p2}px, 0)` }}
      />
      <div
        className={cn("absolute h-64 w-64 rounded-full blur-[90px] ps-anim-float", intensity === "vivid" ? "bg-cyan-400" : "bg-cyan-400")}
        style={{ top: tint3.top as string, left: tint3.left as string, opacity: tint3.opacity, transform: `translate3d(${p3}px, 0, 0)` }}
      />

      {/* extra small blobs */}
      <div
        className={cn("absolute h-40 w-40 rounded-full blur-[80px] ps-anim-float-2", intensity === "vivid" ? "bg-indigo-400" : "bg-indigo-400")}
        style={{ top: extra1.top as string, right: extra1.right as string, opacity: extra1.opacity, transform: `translate3d(${p2 * -0.6}px, ${p2 * 0.6}px, 0)` }}
      />
      <div
        className={cn("absolute h-40 w-40 rounded-full blur-[80px] ps-anim-float", intensity === "vivid" ? "bg-fuchsia-400" : "bg-fuchsia-400")}
        style={{ bottom: extra2.bottom as string, left: extra2.left as string, opacity: extra2.opacity, transform: `translate3d(${p1 * 0.4}px, ${-p1 * 0.4}px, 0)` }}
      />
      <div
        className={cn("absolute h-40 w-40 rounded-full blur-[80px] ps-anim-float-2", intensity === "vivid" ? "bg-cyan-400" : "bg-cyan-400")}
        style={{ top: extra3.top as string, left: extra3.left as string, opacity: extra3.opacity, transform: `translate3d(${p3 * -0.5}px, ${p3 * -0.5}px, 0)` }}
      />

      {/* diagonal soft gradient overlays */}
      <div
        className={cn("absolute inset-0 ps-anim-gradient", intensity === "vivid" ? "opacity-[0.26]" : "opacity-[0.14]")}
        style={{
          transform: `translate3d(0, ${p3 * 0.3}px, 0)`,
          background: "linear-gradient(135deg, rgba(99,102,241,0.2), rgba(236,72,153,0.14) 45%, rgba(34,211,238,0.12) 85%)"
        }}
      />
      <div
        className={cn("absolute inset-0 ps-anim-gradient", intensity === "vivid" ? "opacity-[0.10]" : "opacity-[0.06]")}
        style={{
          transform: `translate3d(${p2 * -0.2}px, ${p2 * 0.2}px, 0)`,
          background: "linear-gradient(220deg, rgba(236,72,153,0.10), rgba(99,102,241,0.12) 40%, rgba(34,211,238,0.08) 90%)"
        }}
      />

      {/* subtle noise overlay */}
      <div
        aria-hidden
        className={cn("absolute inset-0 mix-blend-overlay", intensity === "vivid" ? "opacity-[0.06]" : "opacity-[0.04]")}
        style={{
          backgroundImage:
            "url('data:image/svg+xml;utf8,<svg xmlns=\\'http://www.w3.org/2000/svg\\' width=\\'140\\' height=\\'140\\' viewBox=\\'0 0 140 140\\'><filter id=\\'n\\'><feTurbulence type=\\'fractalNoise\\' baseFrequency=\\'0.8\\' numOctaves=\\'2\\' stitchTiles=\\'stitch\\'/></filter><rect width=\\'100%\\' height=\\'100%\\' filter=\\'url(%23n)\\' opacity=\\'0.3\\'/></svg>')",
          backgroundSize: "auto",
        }}
      />
    </div>
  );
}
